import {
  backendToDisplayMap,
  currencyFields,
  dateRangeMatchTypes,
  daysElapsedMatchTypes,
  displayMatchTypes,
  displayToBackend,
  inputCategories,
  matchTypeToCategory,
  multiValueMatchTypes,
  regionMatchTypes,
} from '../MatchTypeEditor/constants'
import {
  hasBlankValues,
  isBlankValue,
  isEmptyValue,
  isNonBlankValueField,
  isRangeInvalid,
} from '../MatchTypeEditor/helpers'
import { appliesWhenValues, conditionErrorTypes, initCondition, initCriterion, itemScope } from './constants'

export const getConditionTypeOptions = conditionTypes => {
  return Object.entries(conditionTypes).map(([key, config]) => ({
    value: key,
    label: config.label,
    icon: config.icon,
  }))
}

export const resolveConditionTypeConfig = (conditionTypes, condition) => {
  const entry = Object.entries(conditionTypes).find(
    ([, config]) =>
      config.scope === condition.scope && (config.itemContext || null) === (condition.item_context || null)
  )

  return { key: entry?.[0] || '', config: entry?.[1] || null }
}

export const createCondition = config => {
  return {
    ...initCondition,
    scope: config.scope,
    item_context: config.itemContext || null,
    applies_when: config.supportsAppliesWhen ? appliesWhenValues.atLeastOne : null,
    criteria: [initCriterion],
  }
}

export const supportsMultipleCriteria = condition => condition.scope === itemScope

const hasBlankFieldValue = criterion => {
  if (multiValueMatchTypes.includes(criterion.match_type)) return hasBlankValues(criterion.match_values)

  return isBlankValue(criterion.match_value)
}

export const isCriterionIncomplete = criterion => {
  if (!criterion.field || !criterion.match_type) return true

  if (isNonBlankValueField(criterion.field)) return hasBlankFieldValue(criterion)

  if (criterion.match_type === displayMatchTypes.boolean) return false
  if (dateRangeMatchTypes.includes(criterion.match_type)) return false

  if (criterion.match_type === displayMatchTypes.valueInRange) {
    if (isEmptyValue(criterion.min_value) || isEmptyValue(criterion.max_value)) return true

    return isRangeInvalid(criterion.min_value, criterion.max_value)
  }

  if (regionMatchTypes.includes(criterion.match_type)) {
    return !criterion.match_values || (Array.isArray(criterion.match_values) && criterion.match_values.length === 0)
  }

  if (daysElapsedMatchTypes.includes(criterion.match_type)) {
    return isEmptyValue(criterion.days_value)
  }

  if (multiValueMatchTypes.includes(criterion.match_type)) {
    return !Array.isArray(criterion.match_values) || !criterion.match_values.some(v => String(v).trim() !== '')
  }

  return isEmptyValue(criterion.match_value)
}

export const getIncompleteCriteriaIndexes = criteria => {
  return criteria.reduce((indexes, criterion, criterionIndex) => {
    if (isCriterionIncomplete(criterion)) indexes.push(criterionIndex)

    return indexes
  }, [])
}

export const getErrorsFromConditions = conditions => {
  if (!conditions || conditions.length === 0) return []

  return conditions.flatMap((condition, index) => {
    return getIncompleteCriteriaIndexes(condition.criteria).map(criterionIndex => ({
      type: conditionErrorTypes.conditionIncomplete,
      index,
      criterionIndex,
    }))
  })
}

const backendToDisplay = (backendMatchType, excludeMatch, field, booleanFields = []) => {
  if (booleanFields.includes(field)) {
    return displayMatchTypes.boolean
  }

  const mapping = backendToDisplayMap[backendMatchType]

  if (!mapping) return null

  return excludeMatch ? mapping.true : mapping.false
}

const formatDateValue = value => {
  if (!value) return null

  if (typeof value === 'string') return value

  if (value.format) return value.format('YYYY-MM-DD')

  return value
}

const getCurrencyPayload = (criterion, defaultCurrency) => {
  if (!currencyFields.includes(criterion.field)) return {}

  return { match_currency: criterion.match_currency || defaultCurrency }
}

const convertCriterionToBackend = (criterion, defaultCurrency) => {
  const { match_type, exclude_match } = displayToBackend[criterion.match_type]
  const category = matchTypeToCategory[criterion.match_type]

  const base = {
    field: criterion.field,
    match_type,
    exclude_match,
  }

  if (multiValueMatchTypes.includes(criterion.match_type)) {
    return { ...base, match_values: criterion.match_values || [], match_case: criterion.match_case }
  }

  if (regionMatchTypes.includes(criterion.match_type)) {
    const values = (criterion.match_values || []).map(v => (typeof v === 'object' ? v.id : v))

    return { ...base, match_values: values }
  }

  if (dateRangeMatchTypes.includes(criterion.match_type)) {
    return {
      ...base,
      date_range_start: formatDateValue(criterion.date_range_start),
      date_range_end: formatDateValue(criterion.date_range_end),
    }
  }

  if (daysElapsedMatchTypes.includes(criterion.match_type)) {
    return { ...base, days_value: criterion.days_value }
  }

  if (criterion.match_type === displayMatchTypes.valueInRange) {
    return {
      ...base,
      min_value: criterion.min_value,
      max_value: criterion.max_value,
      ...getCurrencyPayload(criterion, defaultCurrency),
    }
  }

  if (category === inputCategories.boolean) {
    return { ...base, match_value: criterion.match_value }
  }

  return {
    ...base,
    match_value: criterion.match_value,
    match_case: criterion.match_case,
    ...getCurrencyPayload(criterion, defaultCurrency),
  }
}

const convertConditionToBackend = (condition, defaultCurrency) => {
  return {
    scope: condition.scope,
    item_context: condition.item_context,
    applies_when: condition.applies_when || undefined,
    applies_to_action: condition.applies_to_action ?? false,
    criteria: condition.criteria.map(criterion => convertCriterionToBackend(criterion, defaultCurrency)),
  }
}

export const transformConditionsToBackend = (conditions = [], { defaultCurrency } = {}) => {
  return conditions.map(condition => convertConditionToBackend(condition, defaultCurrency))
}

const mapCriterionFromBackend = (criterion, booleanFields) => {
  return {
    field: criterion.field,
    match_type: backendToDisplay(criterion.match_type, criterion.exclude_match, criterion.field, booleanFields),
    match_value: criterion.match_value,
    match_values: criterion.match_values,
    match_case: criterion.match_case,
    date_range_start: criterion.date_range_start,
    date_range_end: criterion.date_range_end,
    days_value: criterion.days_value,
    min_value: criterion.min_value,
    max_value: criterion.max_value,
    match_currency: criterion.match_currency,
  }
}

const mapConditionFromBackend = (condition, booleanFields) => {
  return {
    scope: condition.scope,
    item_context: condition.item_context,
    applies_when: condition.applies_when,
    applies_to_action: condition.applies_to_action,
    criteria: condition.criteria.map(criterion => mapCriterionFromBackend(criterion, booleanFields)),
  }
}

export const transformConditionsFromBackend = (conditions = [], { booleanFields } = {}) => {
  return conditions.map(condition => mapConditionFromBackend(condition, booleanFields))
}
